import React, { useState } from 'react';
import { addDays } from 'date-fns';
import { absencesApi } from '@/services/absencesApi';
import { mapAbsenceToTimesheetEntry } from '@/utils/absenceMapper';
import { syncScheduledBookingsAsync, syncScheduledBookingsForWeek, type EmployeeRoleInfo } from '@/services/scheduledBookingService';
import type { Employee, TimesheetEntry } from '@/types/timesheet';
import type { DataMode } from '@/contexts/DataSourceContext';

export interface UseTimesheetScheduleDataParams {
  screenMode: DataMode;
  targetEmployeeId: number;
  currentWeekStart: Date;
  currentEmployee: Employee;
  companyId?: string;
  entries: TimesheetEntry[];
  setEntries: React.Dispatch<React.SetStateAction<TimesheetEntry[]>>;
  isReadOnly: boolean;
  fetchCostCentresForProject: (projectCode: string) => Promise<void>;
}

export interface UseTimesheetScheduleDataResult {
  hasLoadedScheduledBookings: boolean;
  setHasLoadedScheduledBookings: React.Dispatch<React.SetStateAction<boolean>>;
  hasLoadedAbsences: boolean;
  setHasLoadedAbsences: React.Dispatch<React.SetStateAction<boolean>>;
}

/**
 * Loads the supplementary rows the grid displays alongside manual entries:
 * scheduled bookings (3-way synced for editable timesheets) and HR absences
 * (display-only, fetched for every timesheet status).
 */
export function useTimesheetScheduleData({
  screenMode,
  targetEmployeeId,
  currentWeekStart,
  currentEmployee,
  companyId,
  entries,
  setEntries,
  isReadOnly,
  fetchCostCentresForProject,
}: UseTimesheetScheduleDataParams): UseTimesheetScheduleDataResult {
  const [hasLoadedScheduledBookings, setHasLoadedScheduledBookings] = useState(false);
  const [hasLoadedAbsences, setHasLoadedAbsences] = useState(false);

  // Auto-load / sync scheduled bookings when timesheet is opened.
  // For editable timesheets (draft/rejected/recalled): full 3-way sync so that
  //   booking updates and deletions are reflected in the timesheet lines.
  // For read-only timesheets (submitted/approved/locked): skip loading entirely
  //   so that no API calls are made and only persisted data is displayed.
  React.useEffect(() => {
    if (!hasLoadedScheduledBookings && entries.length >= 0) {
      // Auto-load / sync scheduled bookings when timesheet is opened.
      if (isReadOnly) {
        setHasLoadedScheduledBookings(true);
        return;
      }

      const weekEnding = addDays(currentWeekStart, 6);

      // Build employee role info for defaulting the Role field on booking-sourced entries
      const employeeRole: EmployeeRoleInfo = {
        roleId: currentEmployee?.roleId,
        roleName: currentEmployee?.roleId ? currentEmployee?.role : undefined,
      };

      // Helper: trigger cost centre lookups for any project codes that appear in
      // the reconciled entry set but were not previously fetched.
      const fetchNewProjectCostCentres = (reconciled: TimesheetEntry[]) => {
        const projectCodes = new Set<string>();
        reconciled.forEach(e => {
          if (e.projectCode) projectCodes.add(e.projectCode);
        });
        projectCodes.forEach(code => fetchCostCentresForProject(code));
      };

      if (screenMode === 'live') {
        syncScheduledBookingsAsync(targetEmployeeId, weekEnding, entries, employeeRole, companyId)
          .then(reconciledEntries => {
            if (JSON.stringify(reconciledEntries) !== JSON.stringify(entries)) {
              setEntries(reconciledEntries);
              fetchNewProjectCostCentres(reconciledEntries);
            }
            setHasLoadedScheduledBookings(true);
          })
          .catch(error => {
            console.error('Failed to load scheduled bookings:', error);
            setHasLoadedScheduledBookings(true);
          });
      } else {
        const reconciledEntries = syncScheduledBookingsForWeek(targetEmployeeId, weekEnding, entries, employeeRole);

        if (JSON.stringify(reconciledEntries) !== JSON.stringify(entries)) {
          setEntries(reconciledEntries);
          fetchNewProjectCostCentres(reconciledEntries);
        }

        setHasLoadedScheduledBookings(true);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReadOnly, hasLoadedScheduledBookings, entries.length, currentWeekStart, targetEmployeeId, screenMode, companyId]);

  // Auto-load absences from the API when the timesheet week changes (live mode only).
  // Unlike scheduled bookings, absences are fetched for ALL timesheets (including
  // read-only/submitted/approved) because they are display-only supplementary data.
  // Uses employeeId + weekEnding (like scheduled bookings) instead of timesheetId
  // to avoid a timing issue where the timesheet ID is not yet available.
  React.useEffect(() => {
    if (hasLoadedAbsences) return;
    if (screenMode !== 'live') {
      setHasLoadedAbsences(true);
      return;
    }

    let cancelled = false;
    const weekEnding = addDays(currentWeekStart, 6);

    absencesApi.fetchAbsencesForWeek(targetEmployeeId, weekEnding, companyId)
      .then(dtos => {
        if (cancelled) return;
        if (dtos.length === 0) {
          setHasLoadedAbsences(true);
          return;
        }

        const employeeRole: EmployeeRoleInfo = {
          roleId: currentEmployee?.roleId,
          roleName: currentEmployee?.roleId ? currentEmployee?.role : undefined,
        };
        const absenceEntries = dtos.map((dto, i) => mapAbsenceToTimesheetEntry(dto, i, employeeRole));

        setEntries(prev => {
          const existingAbsenceIds = new Set(
            prev.filter(e => e.absenceId).map(e => e.absenceId)
          );
          const newEntries = absenceEntries.filter(
            ae => !existingAbsenceIds.has(ae.absenceId)
          );
          // For entries that already exist, refresh hours from HR (source of truth)
          const updatedPrev = prev.map(e => {
            if (e.absenceId) {
              const freshAbsence = absenceEntries.find(ae => ae.absenceId === e.absenceId);
              if (freshAbsence) {
                return { ...e, hours: freshAbsence.hours, isReadOnly: true, sourceIntegration: freshAbsence.sourceIntegration };
              }
            }
            return e;
          });
          return newEntries.length > 0 ? [...updatedPrev, ...newEntries] : updatedPrev;
        });
        setHasLoadedAbsences(true);
      })
      .catch(() => {
        if (!cancelled) setHasLoadedAbsences(true);
      });

    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasLoadedAbsences, currentWeekStart, targetEmployeeId, screenMode, companyId]);

  return {
    hasLoadedScheduledBookings,
    setHasLoadedScheduledBookings,
    hasLoadedAbsences,
    setHasLoadedAbsences,
  };
}
